import { Component, computed, effect, inject, untracked, ChangeDetectionStrategy } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { DataService } from '../services/data.service';
import { AuthService } from '../services/auth.service';
import { Scan } from '../models/scan.model';
import { aggregateStatusCounts, statusColor } from '../shared/scan-stats';
import { FilterBarComponent, rangeForPreset } from '../shared/filter-bar/filter-bar.component';
import { SummaryComponent } from '../dashboard/summary/summary.component';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartData } from 'chart.js';
import { SettingsService, utcOffsetValue } from '../services/settings.service';
import { ApiService } from '../services/api.service';
import { pieSliceLabels, whiteCanvasBackground } from '../shared/chart-plugins';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterLink } from "@angular/router";
import { toObservable } from '@angular/core/rxjs-interop';
import { SoldUnprocessedCounts } from '../models/landingPage/soldUnprocessedCounts.model';
import { MatDividerModule } from '@angular/material/divider';
import { latestLoad } from '../shared/latest-load';

/** The two inventory wedges. Greyscale, per the card's wireframe. */
const INVENTORY_COLORS = {
  unprocessed: '#646464',
  processed: '#949494'
} as const;

/** A headline metric card. */
interface Kpi {
  label: string;
  value: number;
  /** Optional share of total, 0–100. */
  percent?: number;
  accent: string;
}

type AlertSeverity = 'critical' | 'warning' | 'info' | 'success';

/** A surfaced alert derived from the filtered scans. */
interface AlertItem {
  severity: AlertSeverity;
  icon: string;
  title: string;
  detail: string;
}

/**
 * Summary & Alerts: headline KPIs and a rules-driven alerts feed over the
 * filtered scans, followed by the combined summary charts. Alerts are computed
 * from the Auto-Cleared processing status, the portal's primary product signal.
 */
@Component({
  selector: 'app-summary-alerts',
  imports: [
    MatCardModule,
    MatIconModule,
    MatToolbarModule,
    MatButtonModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    BaseChartDirective,
    RouterLink,
    MatDividerModule
],
  templateUrl: './summary-alerts.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './summary-alerts.component.scss'
})
export class SummaryAlertsComponent {
  private readonly data = inject(DataService);
  readonly auth = inject(AuthService);
  private readonly settings = inject(SettingsService);
  private readonly api = inject(ApiService);

  readonly today = this.formatDate(new Date().toISOString());

  /**
   * "Sites in this report": the count carried by the token, or "All" for an
   * unrestricted account. Grouped in the user's locale, so a four-figure count
   * reads as 9,999 rather than 9999. Anything that isn't a number is shown as
   * it arrived rather than swallowed.
   */
  readonly sitesLabel = computed(() => {
    const sites = this.auth.sites();
    if (!sites) {
      return '—';
    }
    if (sites === 'all') {
      return 'All';
    }
    const count = Number(sites);
    return Number.isFinite(count)
      ? new Intl.NumberFormat(this.settings.locale()).format(count)
      : sites;
  });

  // --- Daily activity chart ---

  /**
   * This page has no date picker: the card is titled for a fixed window, so the
   * range is fixed too. Taken from the filter bar's own preset table so "last 7
   * days" means the same thing here as it does there.
   */
  private readonly chartRange = rangeForPreset('last7');

  chartLoading = false;

  readonly stackedBarChartType = 'bar' as const;

  /** Stable reference: an inline array would re-render the chart every cycle. */
  readonly chartPlugins = [whiteCanvasBackground];

  readonly stackedBarChartData = computed<ChartData<'bar'>>(() => ({
    labels: this.data.activityByDateChartData().dates.map(day => this.formatDateWithoutYear(day)),
    datasets: this.data.activityByDateChartData().activity.map(series => ({
      label: series.status,
      data: series.scans,
      backgroundColor: statusColor(series.status)
    }))
  }));

  readonly stackedBarChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 16
      }
    },
    plugins: {
      // Rendered as chips below the chart instead, so it can carry totals.
      legend: { display: false }
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false }
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: {
          color: '#cacaca',
          drawTicks: false
        }
      }
    }
  };

  /**
   * The chart's legend, rendered as chips: one per status, with its total over
   * the whole window. Colours come from the shared status mapping, so the chips
   * and the bars can never disagree.
   */
  readonly chartLegend = computed(() =>
    this.data.activityByDateChartData().activity.map(series => {
      const color = statusColor(series.status);
      return {
        status: series.status,
        total: series.scans.reduce((sum, count) => sum + count, 0),
        color,
        textColor: readableTextOn(color)
      };
    })
  );

  soldUnprocessedCounts: SoldUnprocessedCounts | undefined;

  soldUnprocessedCountsLoading: boolean = false;

  // --- Unprocessed inventory pie ---

  unprocessedVsProcessedCountsLoading: boolean = false;

  readonly pieChartType = 'pie' as const;

  /** Stable reference — see chartPlugins. */
  readonly inventoryChartPlugins = [whiteCanvasBackground, pieSliceLabels];

  /**
   * Unprocessed vs processed inventory with the derived total and shares.
   *
   * The processed share is the remainder rather than its own rounding, so the
   * two printed percentages always sum to 100 — rounding both independently
   * can produce 101 (12.5% and 87.5%, say). The wedges are drawn from the raw
   * counts, so the display rounding never distorts the geometry.
   */
  readonly inventory = computed(() => {
    const { unprocessedCount, processedCount } = this.data.unprocessedVsProcessedCounts();
    const total = unprocessedCount + processedCount;
    const unprocessedShare = total ? Math.round((unprocessedCount / total) * 100): 0;
    return {
      unprocessed: unprocessedCount,
      processed: processedCount,
      total,
      unprocessedShare,
      processedShare: total ? 100 - unprocessedShare : 0
    };
  });

  readonly inventoryTotalLabel = computed(() => {
    const total = this.inventory().total;
    const formatted = new Intl.NumberFormat(this.settings.locale()).format(total);
    /* return `${formatted} ${total === 1 ? 'vehicle' : 'vehicles'}`; */
    return `${formatted}`;
  });

  readonly inventoryChartData = computed<ChartData<'pie'>>(() => {
    const inventory = this.inventory();
    return {
      labels: ['Unprocessed', 'Processed'],
      datasets: [{
        data: [inventory.unprocessed, inventory.processed],
        backgroundColor: [INVENTORY_COLORS.unprocessed, INVENTORY_COLORS.processed],
        borderColor: '#000',
        borderWidth: 2
      }]
    };
  });

  /**
   * A computed rather than a field so a locale change reaches the slice
   * labels' number formatting; the reference only changes when the locale does.
   */
  readonly inventoryChartOptions = computed<ChartConfiguration<'pie'>['options']>(() => ({
    responsive: true,
    maintainAspectRatio: false,
    locale: this.settings.locale(),
    layout: { padding: 8 },
    plugins: {
      // Drawn as swatch rows beside the chart so the descriptions can wrap.
      legend: { display: false }
    }
  }));

  /** Legend rows, wording from the card's wireframe. Static — no signal reads. */
  readonly inventoryLegend = computed(() => ([
    {
      color: INVENTORY_COLORS.unprocessed,
      fontColor: '#fff',
      label: 'unprocessed',
      text: 'vehicles not yet scanned with the Privacy4Cars app',
      count:  new Intl.NumberFormat(this.settings.locale()).format(this.inventory().unprocessed),
      share: this.inventory().unprocessedShare
    },
    /* {
      color: INVENTORY_COLORS.unprocessed,
      fontColor: '#fff',
      label: 'Unprocessed',
      text: 'vehicles not yet scanned with the Privacy4Cars app',
      count:  new Intl.NumberFormat(this.settings.locale()).format(this.inventory().unprocessed),
      share: this.inventory().unprocessedShare
    }, */
    /* {
      color: INVENTORY_COLORS.processed,
      fontColor: '#fff',
      label: 'Processed',
      text: 'see "Activity by Date" for status of each vehicle',
      count: new Intl.NumberFormat(this.settings.locale()).format(this.inventory().processed),
      share: this.inventory().processedShare
    } */
  ]));

  /**
   * Chart loads — only the latest counts; see latestLoad.
   *
   * Beyond superseding on a timezone change, this matters because the chart
   * slot is shared with the Activity by Date report, which is filtered: the
   * teardown stops a late response from either page landing on the other, so
   * the filtered chart can never appear on this card or vice versa.
   */
  private readonly loadChart = latestLoad<string>(
    timezone => this.api.getProccessedVinsChart(
      toIsoDate(this.chartRange.start),
      toIsoDate(this.chartRange.end),
      utcOffsetValue(timezone, this.chartRange.start),
      false // the summary card always shows the whole account, not the selection
    ),
    active => this.chartLoading = active
  );

  /**
   * Counts loads. They reload on every timezone change, and a closed timezone
   * select changes value on each arrow-key press — so several can be in flight
   * at once, and only the latest may land.
   */
  private readonly loadSoldUnprocessedCounts = latestLoad<string>(
    timezone => this.api.getSoldUnprocessedCounts(utcOffsetValue(timezone)),
    active => this.soldUnprocessedCountsLoading = active
  );

  private readonly loadUnprocessedVsProcessedCounts = latestLoad<string>(
    timezone => this.api.getUnprocessedVsProcessedCounts(utcOffsetValue(timezone)),
    active => this.unprocessedVsProcessedCountsLoading = active
  );

  constructor() {

    toObservable(this.data.soldUnprocessedCounts).subscribe(soldUnprocessedCounts => {
      this.soldUnprocessedCounts = soldUnprocessedCounts;
    })

    // The counts are bucketed server-side using the reporting offset, so a
    // zone change has to reload them. The first run is the initial load.
    effect(() => {

      const timezone = this.settings.timezone();

      untracked(() => this.loadChartData(timezone));

      untracked(() => this.loadSoldUnprocessedCounts(timezone));

      untracked(() => this.loadUnprocessedVsProcessedCounts(timezone));

    });
  }

  private loadChartData(timezone: string): void {
    // Cleared first so the previous page's range never shows here briefly —
    // the chart data is shared with the Activity by Date report.
    this.data.setDefaultActivityByDateChartData();

    this.loadChart(timezone);
  }

  /**
   * A chart bucket as a short axis label. Rebuilt from its date parts and
   * formatted in UTC because a bucket is a bare calendar day, not an instant.
   */
  formatDateWithoutYear(bucket: string): string {
    const [year, month, day] = bucket.slice(0, 10).split('-').map(Number);
    return new Intl.DateTimeFormat(this.settings.locale(), {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC'
    }).format(new Date(Date.UTC(year, month - 1, day)));
  }

  formatDate(iso: string): string {
    return new Intl.DateTimeFormat(this.settings.locale(), {
      dateStyle: 'full',
      /* timeStyle: 'short', */
      /* timeZone: this.settings.timezone() */
    }).format(new Date(iso));
  }

}

/**
 * Black or white, whichever reads better on the given badge colour.
 *
 * Two of the four status colours — the green and the amber — fail WCAG's 3:1
 * against white, so a single hardcoded foreground would leave those counts
 * hard to read. Assumes the six-digit hex the status map uses.
 */
function readableTextOn(hex: string): string {
  const channels = [1, 3, 5]
    .map(offset => parseInt(hex.slice(offset, offset + 2), 16) / 255)
    .map(value => value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4));
  const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  // The crossover where contrast against white equals contrast against black.
  return luminance > 0.179 ? '#000' : '#fff';
}

/**
 * A picker date as YYYY-MM-DD. Uses the local calendar parts rather than
 * toISOString(), which converts to UTC first and can shift the day.
 */
function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
